/*
 * Purple Dot waitlist units land in Shopify inventory, so the theme treats
 * preorder sizes as low stock. Ask Purple Dot which sizes are ON_PREORDER
 * and swap that copy.
 */
const PREORDER_LABEL = () => window.variantStrings?.inventoryPreorder || 'Available for pre-order';
const READY_TIMEOUT_MS = 8000;

function whenPurpleDotReady() {
  return new Promise((resolve) => {
    if (window.PurpleDot?.fetchAvailability) {
      resolve(window.PurpleDot);
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      if (window.PurpleDot?.fetchAvailability) {
        clearInterval(timer);
        resolve(window.PurpleDot);
      } else if (Date.now() - started > READY_TIMEOUT_MS) {
        clearInterval(timer);
        resolve(null);
      }
    }, 100);
  });
}

function isPreorderVariant(variant) {
  return Boolean(variant?.waitlist_id) && Number(variant.available_in_stock) === 0;
}

function swapLowStockStatus(root, label) {
  const status = root.querySelector('.custom-menu-option__status');
  if (!status) return;

  status.classList.remove('is-error');
  status.classList.add('is-preorder');
  status.querySelector('.indicator-last-few')?.remove();

  const textNode = [...status.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
  if (textNode) {
    textNode.textContent = label;
    return;
  }

  const textEl = status.querySelector('span:not([class])') || status.querySelector('span:not(.indicator-last-few):not(.indicator-notify)');
  if (textEl && textEl !== status) textEl.textContent = label;
  else status.textContent = label;
}

function markPreorderOption(option, label) {
  option.removeAttribute('data-low-stock');
  option.setAttribute('data-preorder', '');
  swapLowStockStatus(option, label);
}

function readVariantsJson(scope) {
  const script = scope.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"]');
  if (!script) return [];
  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    return [];
  }
}

async function applyPreorderStockLabels(scope) {
  const productInfo = scope || document.querySelector('product-info');
  if (!productInfo || productInfo.dataset.pdPreorderLabels === 'applied') return;

  const pd = await whenPurpleDotReady();
  if (!pd) return;

  const variantInput = productInfo.querySelector('[name="id"], .product-variant-id');
  const variantId = Number(variantInput?.value);
  if (!variantId) return;

  let availability;
  try {
    availability = await pd.fetchAvailability({ variantId });
  } catch (error) {
    return;
  }

  const preorderIds = new Set(
    (availability?.variants || []).filter(isPreorderVariant).map((variant) => String(variant.id))
  );
  if (preorderIds.size === 0) return;

  const label = PREORDER_LABEL();
  const shopifyVariants = readVariantsJson(productInfo);
  const preorderValues = new Set(
    shopifyVariants.filter((variant) => preorderIds.has(String(variant.id))).flatMap((variant) => {
      return [variant.option1, variant.title].filter(Boolean);
    })
  );

  const roots = [productInfo, ...document.querySelectorAll('.variant-size-picker')];
  roots.forEach((root) => {
    if (!root) return;

    root.querySelectorAll('[data-variant-id]').forEach((option) => {
      if (!preorderIds.has(String(option.dataset.variantId))) return;
      if (!option.querySelector('.custom-menu-option__status.is-error, .indicator-last-few')) return;
      markPreorderOption(option, label);
    });

    root.querySelectorAll('[data-custom-menu-option][data-low-stock]').forEach((option) => {
      if (!preorderValues.has(option.dataset.value)) return;
      markPreorderOption(option, label);
    });

    root.querySelectorAll('custom-select').forEach((select) => {
      if (typeof select.syncFromSelect === 'function') select.syncFromSelect();
    });
  });

  productInfo.dataset.pdPreorderLabels = 'applied';
}

function watchForPurpleDot(productInfo) {
  if (!productInfo) return;

  applyPreorderStockLabels(productInfo);

  const observer = new MutationObserver(() => {
    if (!productInfo.classList.contains('x-pd-atc-form--preorder')) return;
    if (productInfo.dataset.pdPreorderLabels === 'applied') return;
    applyPreorderStockLabels(productInfo);
  });
  observer.observe(productInfo, { attributes: true, attributeFilter: ['class'] });
}

function init() {
  document.querySelectorAll('product-info').forEach((productInfo) => watchForPurpleDot(productInfo));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('product-info').forEach((productInfo) => watchForPurpleDot(productInfo));
});
